import React, { useRef, useEffect, useCallback, useState } from 'react';
import type { VisualMode, QualityTier } from '../types';

interface UseAnimationLoopOptions {
  mode: VisualMode;
  reducedMotion: boolean;
  quality: QualityTier;
  animationSpeed: number;
  onTick: (deltaTime: number, time: number) => void;
  enabled?: boolean;
}

export function useAnimationLoop({
  mode,
  reducedMotion,
  quality,
  animationSpeed,
  onTick,
  enabled = true,
}: UseAnimationLoopOptions) {
  const requestRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number>(0);
  const accumulatedTimeRef = useRef<number>(0);

  const tick = useCallback(
    (currentTime: number) => {
      if (!enabled) return;

      const deltaTime = (currentTime - lastTimeRef.current) / 1000;
      lastTimeRef.current = currentTime;

      const clampedDelta = Math.min(deltaTime, 1 / 30);
      const scaledDelta = clampedDelta * animationSpeed;

      accumulatedTimeRef.current += scaledDelta;
      onTick(scaledDelta, accumulatedTimeRef.current);

      requestRef.current = requestAnimationFrame(tick);
    },
    [enabled, animationSpeed, onTick]
  );

  useEffect(() => {
    if (!enabled) return;

    lastTimeRef.current = performance.now();
    accumulatedTimeRef.current = 0;
    requestRef.current = requestAnimationFrame(tick);

    return () => {
      if (requestRef.current !== null) {
        cancelAnimationFrame(requestRef.current);
        requestRef.current = null;
      }
    };
  }, [enabled, tick, mode, reducedMotion, quality, animationSpeed]);
}

export function useReducedMotion(): boolean {
  const [reduced, setReduced] = React.useState(false);

  React.useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mediaQuery.matches);

    const handler = (event: MediaQueryListEvent) => setReduced(event.matches);
    mediaQuery.addEventListener('change', handler);

    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return reduced;
}